import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import {
  COLOR_VALUE,
  RECIPE_TYPE,
  RESTORATION_METHOD,
} from '@/pages/getRecipe/matisseLite/dentin&enamel/utils/constants'
import { FORM_FIELDS } from '@/pages/getRecipe/matisseLite/dentin&enamel/utils/formTypes'

import { PLACEMENT } from './appSlice'
import { CrownInput } from './stainingStudio'

interface stainingCorrectionRecipe {
  [PLACEMENT.LEFT]: { [key: string]: { input: CrownInput; output: any } }
  [PLACEMENT.RIGHT]: { [key: string]: { input: CrownInput; output: any } }
}
export interface fullLayeringState {
  selectedStainingCorrectionKey: string | null
  stainingCorrectionRecipe: stainingCorrectionRecipe | null
  selectedStainingCorrectionBrand: string | null

  // Dentin & Enamel
  shadeGuideActiveStep: number
  form: { [key in keyof FormData]?: any }
  shadeGuideRecipeOutput: any
  restorationMethodOnRecipeGenerate: string | null
  selectedPickerKey: string
  isNextClicked: boolean
}

const initialState: fullLayeringState = {
  selectedStainingCorrectionKey: null,
  stainingCorrectionRecipe: { [PLACEMENT.LEFT]: {}, [PLACEMENT.RIGHT]: {} },
  selectedStainingCorrectionBrand: null,

  // Dentin & Enamel
  shadeGuideActiveStep: 0,
  form: {
    [FORM_FIELDS.RECIPE_TYPE]: RECIPE_TYPE.SINGLE_ZONE,
    [FORM_FIELDS.RESTORATION_METHOD]: RESTORATION_METHOD.STAINING,
    [FORM_FIELDS.PREPARATION_COLOR]: COLOR_VALUE.Vita3DMaster,
    [FORM_FIELDS.TARGET_COLOR]: COLOR_VALUE.Vita3DMaster,
    [FORM_FIELDS.AVAILABLE_SPACE_CERVICAL]: 0.5,
    [FORM_FIELDS.AVAILABLE_SPACE_INCISAL]: 0.5,
    [FORM_FIELDS.AVAILABLE_SPACE_MIDDLE]: 0.5,
    [FORM_FIELDS.PREP_COLOR]: COLOR_VALUE.LabColor,
  },
  shadeGuideRecipeOutput: null,
  restorationMethodOnRecipeGenerate: 'layering',
  selectedPickerKey: '',
  isNextClicked: false,
}

export const matisseLiteSlice = createSlice({
  name: 'matisseLite',
  initialState,
  reducers: {
    setSelectedStainingCorrectionKey: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      return { ...state, selectedStainingCorrectionKey: action.payload }
    },
    setSelectedStainngCorrectionBrand: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      return { ...state, selectedStainingCorrectionBrand: action.payload }
    },
    setStainingCorrectionRecipe: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        key: string
        data: { input?: CrownInput; output?: any }
      }>,
    ) => {
      const placement = action.payload
        .placement as keyof stainingCorrectionRecipe
      const currentRecipe = state.stainingCorrectionRecipe || {
        [PLACEMENT.LEFT]: {},
        [PLACEMENT.RIGHT]: {},
      }

      return {
        ...state,
        stainingCorrectionRecipe: {
          ...currentRecipe,
          [placement]: {
            ...currentRecipe[placement],
            [action.payload.key]: {
              ...currentRecipe[placement][action.payload.key],
              ...action.payload.data,
            },
          },
        },
      }
    },
    clearStainingCorrectionState: () => initialState,
    removeStainingCorrectionKey: (
      state,
      action: PayloadAction<{ key: string }>,
    ) => {
      if (state?.stainingCorrectionRecipe?.[PLACEMENT.LEFT]) {
        delete state.stainingCorrectionRecipe[PLACEMENT.LEFT][
          action.payload.key
        ]
      }
      if (state?.stainingCorrectionRecipe?.[PLACEMENT.RIGHT]) {
        delete state.stainingCorrectionRecipe[PLACEMENT.RIGHT][
          action.payload.key
        ]
      }
    },

    // Dentin & Enamel
    setShadeGuideActiveStep: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        shadeGuideActiveStep: action.payload,
      }
    },
    updateshadeGuideFormValue: (
      state,
      action: PayloadAction<{ name: keyof FormData; value: any }>,
    ) => {
      return {
        ...state,
        form: {
          ...state.form,
          [action.payload.name]: action.payload.value,
        },
      }
    },
    setShadeGuideRecipeOutput: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        shadeGuideRecipeOutput: action.payload,
      }
    },
    setRestorationMethodOnRecipeGenerate: (
      state,
      action: PayloadAction<string>,
    ) => {
      return {
        ...state,
        restorationMethodOnRecipeGenerate: action.payload,
      }
    },
    resetState: (state) => {
      return {
        ...initialState,
        shadeGuideRecipeOutput: state.shadeGuideRecipeOutput,
        restorationMethodOnRecipeGenerate:
          state.restorationMethodOnRecipeGenerate,
      }
    },
    setSelectedPickerKey: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        selectedPickerKey: action.payload,
      }
    },
    setIsNextClicked(state, action: PayloadAction<boolean>) {
      state.isNextClicked = action.payload
    },
  },
})

export const {
  setSelectedStainingCorrectionKey,
  setSelectedStainngCorrectionBrand,
  setStainingCorrectionRecipe,
  clearStainingCorrectionState,
  removeStainingCorrectionKey,

  // Dentin & Enamel
  setShadeGuideActiveStep,
  updateshadeGuideFormValue,
  setShadeGuideRecipeOutput,
  setRestorationMethodOnRecipeGenerate,
  resetState,
  setSelectedPickerKey,
  setIsNextClicked,
} = matisseLiteSlice.actions
export default matisseLiteSlice.reducer
